/**
 * Country-name comparison.
 *
 * External geo sources (the deforestation warning API, reverse geocoders, bulk
 * imports) name countries differently from the DDS reports and farms they get
 * compared against — the warning API says "Ivory Coast" where a report's
 * countryOfProduction holds "Côte d'Ivoire", and imported spreadsheets carry
 * typos. A raw === / Array.includes() reads all of that as a location mismatch
 * and raises a false wrong-country warning.
 *
 * Matching runs in three steps: normalize (case/accents/punctuation), look up
 * known synonyms, then a conservative typo pass.
 *
 * The two data blocks below mirror `src/config/country.ts` in
 * CF_Deforestation_Farmer_Service, whose helper (`src/helpers/country-name.helper.ts`)
 * runs the same three steps. Keep them in sync: a synonym added on one side only
 * makes this panel and the backend's compliance counter disagree.
 */

// Names for the SAME country; first entry is canonical. Never mix two distinct
// countries into one group: the two Congos stay apart, and bare "Congo" is
// Congo-Brazzaville.
const ALIAS_GROUPS = [
  ['Aland', 'Åland Islands'],
  ['Bolivia', 'Plurinational State of Bolivia'],
  ['British Virgin Islands', 'Virgin Islands, British'],
  ['Brunei', 'Brunei Darussalam'],
  ['Cape Verde', 'Cabo Verde'],
  ['Czech Republic', 'Czechia'],
  ['Democratic Republic of the Congo', 'DR Congo', 'DRC', 'Congo-Kinshasa', 'Congo (DRC)'],
  ['East Timor', 'Timor-Leste', 'Democratic Republic of Timor-Leste'],
  ['Falkland Islands', 'Falkland Islands (Islas Malvinas)', 'Malvinas'],
  ['Federated States of Micronesia', 'Micronesia'],
  ['Fiji', 'Fiji Islands'],
  ['Gambia', 'The Gambia', 'Gambia, The'],
  ['Hong Kong S.A.R.', 'Hong Kong'],
  ['Iran', 'Islamic Republic of Iran'],
  ['Isle of Man', 'Man, Isle of'],
  ['Ivory Coast', "Côte d'Ivoire"],
  ['Laos', 'Lao PDR', "Lao People's Democratic Republic"],
  ['Macau S.A.R', 'Macau', 'Macao', 'Macao SAR'],
  ['Macedonia', 'North Macedonia', 'Macedonia, Former Yugoslav Republic of', 'FYROM'],
  ['Moldova', 'Republic of Moldova'],
  ['Myanmar', 'Burma'],
  ['Netherlands', 'Holland', 'The Netherlands'],
  ['North Korea', "Democratic People's Republic of Korea", 'Korea, North'],
  ['Republic of Serbia', 'Serbia'],
  ['Republic of the Congo', 'Congo-Brazzaville', 'Congo'],
  ['Russia', 'Russian Federation'],
  ['S. Sudan', 'South Sudan'],
  ['Saint Helena', 'Saint Helena, Ascension and Tristan da Cunha'],
  ['Saint Martin', 'Saint Martin (French part)'],
  ['Sint Maarten', 'Sint Maarten (Dutch part)'],
  ['South Korea', 'Republic of Korea', 'Korea, South', 'Korea'],
  ['Swaziland', 'Eswatini'],
  ['Syria', 'Syrian Arab Republic'],
  ['The Bahamas', 'Bahamas', 'Bahamas, The'],
  ['Turkey', 'Türkiye'],
  ['United Arab Emirates', 'UAE'],
  ['United Kingdom', 'Great Britain', 'UK', 'United Kingdom of Great Britain and Northern Ireland'],
  ['United Republic of Tanzania', 'Tanzania'],
  ['United States of America', 'United States', 'America', 'USA'],
  ['United States Virgin Islands', 'Virgin Islands, U.S.', 'U.S. Virgin Islands'],
  ['Vatican', 'Vatican City', 'Holy See'],
  ['Venezuela', 'Bolivarian Republic of Venezuela'],
  ['Vietnam', 'Viet Nam'],
];

// Canonical country names — the vocabulary the typo pass matches against.
const COUNTRY_NAMES = [
  'Afghanistan', 'Aland', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla',
  'Antarctica', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Aruba', 'Australia', 'Austria',
  'Azerbaijan', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin',
  'Bermuda', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil',
  'British Indian Ocean Territory', 'British Virgin Islands', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Cayman Islands',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Cook Islands',
  'Costa Rica', 'Croatia', 'Curaçao', 'Czech Republic', 'Democratic Republic of the Congo',
  'Denmark', 'Dhekelia Sovereign Base Area', 'Djibouti', 'Dominica', 'Dominican Republic',
  'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Ethiopia', 'Falkland Islands', 'Faroe Islands', 'Federated States of Micronesia', 'Fiji',
  'Finland', 'France', 'French Polynesia', 'French Southern and Antarctic Lands', 'Gabon',
  'Gambia', 'Gaza Strip', 'Georgia', 'Germany', 'Ghana', 'Gibraltar', 'Greece', 'Greenland',
  'Grenada', 'Guam', 'Guatemala', 'Guernsey', 'Guinea', 'Guinea Bissau', 'Guyana', 'Haiti',
  'Heard Island and McDonald Islands', 'Honduras', 'Hong Kong S.A.R.', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Isle of Man', 'Israel', 'Italy',
  'Ivory Coast', 'Jamaica', 'Japan', 'Jersey', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
  'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
  'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macau S.A.R', 'Macedonia', 'Madagascar', 'Malawi',
  'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
  'Mexico', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Montserrat', 'Morocco', 'Mozambique',
  'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Caledonia', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'Niue', 'Norfolk Island', 'North Korea',
  'Northern Mariana Islands', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea',
  'Paraguay', 'Peru', 'Philippines', 'Pitcairn Islands', 'Poland', 'Portugal', 'Puerto Rico',
  'Qatar', 'Republic of Serbia', 'Republic of the Congo', 'Romania', 'Russia', 'Rwanda',
  'S. Sudan', 'Saint Barthelemy', 'Saint Helena', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Martin', 'Saint Pierre and Miquelon', 'Saint Vincent and the Grenadines', 'Samoa',
  'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Seychelles', 'Sierra Leone',
  'Singapore', 'Sint Maarten', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
  'South Africa', 'South Georgia and the Islands', 'South Korea', 'Spain', 'Sri Lanka', 'Sudan',
  'Suriname', 'Swaziland', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Thailand',
  'The Bahamas', 'Togo', 'Tokelau', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
  'Turkmenistan', 'Turks and Caicos Islands', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United Republic of Tanzania',
  'United States Minor Outlying Islands', 'United States of America',
  'United States Virgin Islands', 'Uruguay', 'US Naval Base Guantanamo Bay', 'Uzbekistan',
  'Vanuatu', 'Vatican', 'Venezuela', 'Vietnam', 'Wallis and Futuna', 'Western Sahara', 'Yemen',
  'Zambia', 'Zimbabwe',
];

const COMBINING_MARKS = /[\u0300-\u036f]/g;

// NFD splits an accented letter into base + combining mark, so dropping the
// marks folds "Côte" to "Cote".
function normalizeCountryName(value) {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/[^a-z0-9]/g, '');
}

// Every known spelling -> the canonical key of the country it belongs to.
const CANONICAL_BY_NAME = new Map();
ALIAS_GROUPS.forEach((group) => {
  const canonical = normalizeCountryName(group[0]);
  group.forEach((name) => CANONICAL_BY_NAME.set(normalizeCountryName(name), canonical));
});
COUNTRY_NAMES.forEach((name) => {
  const key = normalizeCountryName(name);
  if (key && !CANONICAL_BY_NAME.has(key)) CANONICAL_BY_NAME.set(key, key);
});

// Typo candidates only: shorter strings cannot survive a wrong character.
const TYPO_CANDIDATES = [...CANONICAL_BY_NAME].filter(([key]) => key.length >= 4);

/**
 * Typo budget. Short names are left alone — one wrong character in "mali",
 * "iran" or "chad" lands on a different real country as often as not.
 */
function typoBudget(key) {
  if (key.length >= 10) return 2;
  if (key.length >= 4) return 1;
  return 0;
}

/** Levenshtein distance, abandoned as soon as it exceeds `max`. */
function editDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const substitution = prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, substitution);
      if (row[j] < rowMin) rowMin = row[j];
    }
    if (rowMin > max) return max + 1;
    prev = row;
  }
  return prev[b.length];
}

const TYPO_CACHE = new Map();

/**
 * Canonical key of the nearest country within the typo budget, but only when it
 * is a clear winner: a tie between two countries ("irak" sits one edit from
 * both Iraq and Iran) resolves to nothing rather than to a guess.
 */
function resolveTypo(key) {
  const max = typoBudget(key);
  if (!max) return '';
  if (TYPO_CACHE.has(key)) return TYPO_CACHE.get(key);

  let best = max + 1;
  let winner = '';
  let ambiguous = false;
  TYPO_CANDIDATES.forEach(([candidate, canonical]) => {
    const distance = editDistance(candidate, key, max);
    if (distance > max) return;
    if (distance < best) {
      best = distance;
      winner = canonical;
      ambiguous = false;
    } else if (distance === best && canonical !== winner) {
      ambiguous = true;
    }
  });
  const resolved = ambiguous ? '' : winner;
  TYPO_CACHE.set(key, resolved);
  return resolved;
}

/** Alias-collapsed comparison key: every spelling of one country shares it. */
function countryNameKey(value) {
  const key = normalizeCountryName(value);
  if (!key) return '';
  return CANONICAL_BY_NAME.get(key) || resolveTypo(key) || key;
}

/**
 * True when both values name the same country, ignoring case, accents,
 * punctuation, known synonyms and small misspellings. Empty values never match.
 */
export function countryNamesMatch(a, b) {
  const keyA = countryNameKey(a);
  const keyB = countryNameKey(b);
  return !!keyA && keyA === keyB;
}

/** Alias-aware replacement for `list.includes(country)`. */
export function countryListIncludesCountry(list, country) {
  if (!Array.isArray(list) || !list.length) return false;
  return list.some((item) => countryNamesMatch(item, country));
}
